<template>
  <div class="login-page">
    <div class="login-container">
      <!-- Logo section -->
      <div class="brand-section">
        <div class="logo-container">
          <img
            src="../assets/logo.svg"
            alt="Mail Client Logo"
            class="brand-logo"
          />
        </div>
        <h1 class="brand-name">Fuli Mail Client</h1>
      </div>

      <!-- Login card -->
      <div class="login-card">
        <h2 class="login-title">Welcome back</h2>
        <p class="login-subtitle">Sign in to access your emails</p>

        <!-- Error message -->
        <div v-if="error" class="error-alert">
          <div class="error-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
          </div>
          <p class="error-message">{{ error }}</p>
        </div>

        <!-- Login form -->
        <LoginForm :loading="loading" @submit="handleLogin" />
      </div>

      <!-- Version info -->
      <div class="version-info">
        <p>Version 1.0.0 — &copy; {{ currentYear }} Fuli Mail Client</p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import { useRouter, useRoute } from "vue-router";
import { TokenManager } from "@/services/api";
import LoginForm from "@/components/LoginForm.vue";

export default {
  name: "LoginView",
  components: {
    LoginForm,
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const loading = ref(false);
    const error = ref("");

    // Get current year for copyright
    const currentYear = computed(() => new Date().getFullYear());

    // Check if user is already logged in on component mount
    onMounted(() => {
      if (TokenManager.hasValidTokens()) {
        // User has valid tokens, redirect to dashboard or intended route
        const redirect = route.query.redirect || "/dashboard";
        router.push(redirect);
      }
    });

    const handleLogin = async (credentials) => {
      loading.value = true;
      error.value = "";

      try {
        // Call the login action which should return tokens
        const response = await store.dispatch("login", credentials);

        // Ensure tokens are properly stored
        if (response && response.access && response.refresh) {
          TokenManager.setTokens(response.access, response.refresh);
        } else if (response && response.token) {
          // Fallback for backends that return a single token field
          TokenManager.setTokens(
            response.token,
            response.refresh_token || response.refresh
          );
        }

        // Redirect to intended route or dashboard
        const redirect = route.query.redirect || "/dashboard";
        await router.push(redirect);
      } catch (err) {
        console.error("Login error:", err);

        // Clear any partial tokens on login failure
        TokenManager.clearTokens();

        // Handle different types of login errors
        if (err.response) {
          if (err.response.status === 401) {
            error.value = "Invalid username or password. Please try again.";
          } else if (err.response.status === 403) {
            error.value = "Account is disabled. Please contact support.";
          } else if (err.response.status >= 500) {
            error.value = "Server error. Please try again later.";
          } else {
            error.value =
              err.response.data?.detail ||
              err.response.data?.message ||
              "Authentication failed. Please try again.";
          }
        } else if (err.request) {
          error.value =
            "Network error. Please check your connection and try again.";
        } else {
          error.value =
            err.message || "An unexpected error occurred. Please try again.";
        }
      } finally {
        loading.value = false;
      }
    };

    return {
      loading,
      error,
      currentYear,
      handleLogin,
    };
  },
};
</script>

<style lang="scss">
// Variables
$primary-color: #3b82f6;
$primary-hover: #2563eb;
$primary-light: #eff6ff;

$text-primary: #111827;
$text-secondary: #4b5563;
$text-muted: #6b7280;
$text-light: #9ca3af;

$error-color: #ef4444;
$error-light: #fee2e2;
$error-dark: #b91c1c;

$link-color: #3b82f6;
$link-hover: #2563eb;

$border-color: #e5e7eb;
$border-dark: #d1d5db;

$bg-white: #ffffff;
$bg-gray: #f3f4f6;
$bg-light: #f9fafb;

$shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.05);
$shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
$shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
  0 2px 4px -1px rgba(0, 0, 0, 0.06);
$shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
  0 4px 6px -4px rgba(0, 0, 0, 0.1);
$shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
  0 10px 10px -5px rgba(0, 0, 0, 0.04);

$radius-sm: 0.25rem;
$radius: 0.375rem;
$radius-md: 0.5rem;
$radius-lg: 0.75rem;
$radius-full: 9999px;

$transition: all 0.2s ease;

// Login page
.login-page {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background-color: $bg-gray;
  background-image: radial-gradient(
      circle at 25% 25%,
      rgba(255, 255, 255, 0.4) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 75% 75%,
      rgba(209, 213, 219, 0.4) 0%,
      transparent 50%
    );
  padding: 1.5rem;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica,
    Arial, sans-serif;
}

.login-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 32rem;
}

// Brand section
.brand-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 2rem;
}

.logo-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 4rem;
  height: 4rem;
  background-color: $primary-light;
  border-radius: $radius-full;
  margin-bottom: 1rem;
  box-shadow: $shadow-md;
}

.brand-logo {
  width: 2.5rem;
  height: 2.5rem;
}

.brand-name {
  font-size: 1.5rem;
  font-weight: 700;
  color: $text-primary;
  margin: 0;
}

// Login card
.login-card {
  width: 100%;
  background-color: $bg-white;
  border-radius: $radius-lg;
  box-shadow: $shadow-xl;
  padding: 2rem;
  margin-bottom: 2rem;

  @media (max-width: 640px) {
    padding: 1.5rem;
  }
}

.login-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: $text-primary;
  margin: 0 0 0.5rem 0;
  text-align: center;
}

.login-subtitle {
  font-size: 1rem;
  color: $text-secondary;
  margin: 0 0 2rem 0;
  text-align: center;
}

// Error alert
.error-alert {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  background-color: $error-light;
  border-radius: $radius;
  padding: 1rem;
  margin-bottom: 1.5rem;

  .error-icon {
    flex-shrink: 0;
    color: $error-color;

    svg {
      width: 1.25rem;
      height: 1.25rem;
    }
  }

  .error-message {
    color: $error-dark;
    font-size: 0.875rem;
    margin: 0;
    line-height: 1.5;
  }
}

// Login footer
.login-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1.5rem;
  gap: 0.5rem;

  .help-link {
    color: $link-color;
    font-size: 0.875rem;
    text-decoration: none;
    transition: $transition;

    &:hover {
      color: $link-hover;
      text-decoration: underline;
    }
  }

  .separator {
    color: $text-light;
    font-size: 0.875rem;
  }
}

// Version info
.version-info {
  color: $text-light;
  font-size: 0.75rem;
  text-align: center;

  p {
    margin: 0;
  }
}
</style>
